import { useEffect, useState } from 'react';
import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { SortOrder } from 'antd/lib/table/interface';
import { arrowDownPurple, arrowUpPurple } from 'assets/images';
import { SalesService } from 'services/sales-service';
import { getSalesFilter, setSalesListFilter } from 'store/sales';
import { SALES_BY_TRANSACTION_CODE } from 'utils/constants/end-points';
import convertBalance from 'utils/helpers/convert-balance';
import { formatDate } from 'utils/helpers/date-time';
import { getDefaultSortParams, SorterConfig } from 'utils/helpers/table-sorting';
import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { IdType, Sorting } from 'utils/types/common';
import { SaleDTO, SalesFilter, SalesListOrderBy } from 'utils/types/sales';
import './SalesTable.scss';

type Props = {
	dataSource: SaleDTO[];
};

type TableItem = SaleDTO & { history: SaleDTO[] };

const getSortOrder = (sorting: Sorting): SortOrder => sorting === Sorting.ASC ? 'ascend' : 'descend';

const SalesTable = ({ dataSource }: Props): JSX.Element => {
	const dispatch = useAppDispatch();

	const [data, setData] = useState<TableItem[]>([]);
	const [expandedKey, setExpandedKey] = useState<IdType | null>(null);

	useEffect(() => {
		setExpandedKey(null);
		setData(dataSource.map(item => ({ ...item, history: [] })));
	}, [dataSource]);

	const salesFilter: SalesFilter = useAppSelector(getSalesFilter);
	const { orderBy, sorting } = salesFilter;

	const getConfig = (filterOrderBy: SalesListOrderBy): SorterConfig<TableItem> => ({
		...getDefaultSortParams(),
		sortOrder: orderBy === filterOrderBy ? getSortOrder(sorting) : undefined,
		onHeaderCell: () => ({
			onClick: e => {
				e.preventDefault();
				dispatch(setSalesListFilter({
					...salesFilter,
					orderBy: filterOrderBy,
					sorting: sorting === Sorting.ASC ? Sorting.DESC : Sorting.ASC,
				}));
			},
		}),
	});

	const getSorterConfig = (column: keyof SaleDTO): SorterConfig<TableItem> => {
		switch (column) {
			case 'price': {
				return getConfig(SalesListOrderBy.PRICE);
			}
			case 'purchaseDate': {
				return getConfig(SalesListOrderBy.PURCHASE_DATE);
			}
			case 'warrantyDate': {
				return getConfig(SalesListOrderBy.WARRANTY_DATE);
			}
			default: return {};
		}
	};

	const columns: ColumnsType<TableItem> = [
		{
			title: 'Transaction Code',
			dataIndex: 'hotmartTransactionCode',
			key: 'hotmartTransactionCode',
			fixed: 'left',
			width: 250,
		},
		{
			title: 'User Name',
			dataIndex: 'userName',
			key: 'userName',
			width: 250,
		},
		{
			title: 'Client Name',
			dataIndex: 'clientName',
			key: 'clientName',
			width: 250,
		},
		{
			title: 'Purchase Date',
			dataIndex: 'purchaseDate',
			key: 'purchaseDate',
			width: 120,
			render: (_, record) => (
				<div>
					{formatDate(record.purchaseDate)}
				</div>
			),
			...getSorterConfig('purchaseDate'),
		},
		{
			title: 'Price',
			dataIndex: 'price',
			key: 'price',
			width: 120,
			render: (_, record) => (
				<div>
					{convertBalance(record.price)}
				</div>
			),
			...getSorterConfig('price'),
		},
		{
			title: 'Commission',
			dataIndex: 'commission',
			key: 'commission',
			width: 120,
			render: (_, record) => (
				<div>
					{convertBalance(record.commission ?? 0)}
				</div>
			),
		},
		{
			title: 'Product',
			dataIndex: 'productName',
			key: 'productName',
			width: 200,
		},
		{
			title: 'Purchase status',
			dataIndex: 'status',
			key: 'status',
			width: 150,
		},
		{
			title: 'Payment Type',
			dataIndex: 'paymentType',
			key: 'paymentType',
			width: 150,
		},
		{
			title: 'Refund period',
			dataIndex: 'warrantyDate',
			key: 'warrantyDate',
			width: 120,
			render: (_, record) => (
				<div>
					{record.warrantyDate && formatDate(record.warrantyDate)}
				</div>
			),
			...getSorterConfig('warrantyDate'),
		},
	];

	return (
		<Table
			className="sales-table"
			columns={columns}
			dataSource={data}
			expandable={{
				childrenColumnName: 'history',
				expandedRowKeys: expandedKey ? [expandedKey] : undefined,
				expandIcon: ({ expanded, onExpand, record }) => record.history ? (
					<img
						alt={expanded ? 'arrow-up-purple' : 'arrow-down-purple'}
						className="sales-table__expand-icon"
						onClick={e => onExpand(record, e)}
						src={expanded ? arrowUpPurple : arrowDownPurple}
					/>
				) : undefined,
				onExpand: (expanded, record) => {
					if (expanded && !record.history?.length) {
						const { hotmartTransactionCode } = record;
						const fetchInstance = new SalesService(SALES_BY_TRANSACTION_CODE);

						fetchInstance.getSales({ hotmartTransactionCode }).then(history => {
							setData(data.map(item => item.id === record.id ? { ...item, history } : item));
						});
					}

					setExpandedKey(expanded ? record.id : null);
				},
			}}
			onRow={(record) => expandedKey === record.id ? ({ className: 'sales-table__row_expanded' }) : {}}
			rowKey="id"
			scroll={{ y: '50vh' }}
		/>
	);
};

export default SalesTable;
